import { createSelector } from 'reselect';
import { TicketKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import {
    AssigneeChangedEvent,
    CallRecordTicketEvent,
    ConversationMessageEvent,
    ConversationMessageKey,
    EmailEventItem,
    OmniCallEventKey,
    TicketActionEvent,
    TicketActionEventKey,
    TicketEvent,
    TicketEventKey,
    eventIsCallRecordEvent,
} from 'flow/types/TicketEventData';

export const selectEvents = (state: State, key: TicketKey) =>
    state.tickets.getIn(['ticketEvents', 'dataStatic', key]);

export const selectHasFetchedEvents = (state: State, key: TicketKey) =>
    state.tickets.hasIn(['ticketEvents', 'dataStatic', key]);

const selectEventDataStatic = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketEventKey,
) => state.tickets.getIn(['ticketEvents', 'dataStatic', ticketKey, eventKey]);

const selectMessageEventDataStatic = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
): TypedMap<ConversationMessageEvent> | undefined =>
    state.tickets.getIn(['ticketEvents', 'dataStatic', ticketKey, eventKey]) as
        | TypedMap<ConversationMessageEvent>
        | undefined;

const selectEmailMessageEventDataStatic = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) =>
    state.tickets.getIn(['ticketEvents', 'dataStatic', ticketKey, eventKey]) as
        | TypedMap<EmailEventItem>
        | undefined;

export const selectEventMessageText = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => selectMessageEventDataStatic(state, ticketKey, eventKey)?.get('message');

export const selectEmailMessageSenderName = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) =>
    selectEmailMessageEventDataStatic(state, ticketKey, eventKey)?.getIn([
        'senderData',
        'name',
    ]);

export const selectEmailMessageToRecipients = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) =>
    selectEmailMessageEventDataStatic(state, ticketKey, eventKey)?.get(
        'toRecipients',
    );

export const selectEmailMessageCcRecipients = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) =>
    selectEmailMessageEventDataStatic(state, ticketKey, eventKey)?.get(
        'ccRecipients',
    );

export const selectEventMessageStylings = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => selectMessageEventDataStatic(state, ticketKey, eventKey)?.get('stylings');

// A note that arrived by push has no stylings until it is fetched. A note
// that came from the API, such as the response to its own send, has them.
export const selectMessageEventLacksStylings = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => {
    const event = selectMessageEventDataStatic(state, ticketKey, eventKey);
    return !!event && !event.get('stylings');
};

const selectTicketActionEventDataStatic = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketActionEventKey,
) =>
    state.tickets.getIn(['ticketEvents', 'dataStatic', ticketKey, eventKey]) as
        | TypedMap<TicketActionEvent>
        | undefined;

const selectAssigneeChangedEventDataStatic = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketActionEventKey,
) =>
    state.tickets.getIn(['ticketEvents', 'dataStatic', ticketKey, eventKey]) as
        | TypedMap<AssigneeChangedEvent>
        | undefined;

export const selectTicketActionEventPrevValue = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketActionEventKey,
) =>
    selectTicketActionEventDataStatic(state, ticketKey, eventKey)?.get(
        'prevValue',
    );

export const selectTicketActionEventNewValue = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketActionEventKey,
) =>
    selectTicketActionEventDataStatic(state, ticketKey, eventKey)?.get(
        'newValue',
    );

export const selectTicketActionEventActor = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketActionEventKey,
) =>
    selectTicketActionEventDataStatic(state, ticketKey, eventKey)?.get('actor');

export const selectAssigneeChangedEventPrevValue = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketActionEventKey,
) =>
    selectAssigneeChangedEventDataStatic(state, ticketKey, eventKey)?.get(
        'prevValue',
    );

export const selectAssigneeChangedEventNewValue = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketActionEventKey,
) =>
    selectAssigneeChangedEventDataStatic(state, ticketKey, eventKey)?.get(
        'newValue',
    );

export const selectEventDTOSubtype = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketEventKey,
) => selectEventDataStatic(state, ticketKey, eventKey)?.get('DTOSubtype');

export const selectEventTimestamp = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketEventKey,
) => selectEventDataStatic(state, ticketKey, eventKey)?.get('timeStamp');

const selectCallEventDataStatic = (
    state: State,
    ticketKey: TicketKey,
    eventKey: OmniCallEventKey,
) =>
    state.tickets.getIn(['ticketEvents', 'dataStatic', ticketKey, eventKey]) as
        | TypedMap<CallRecordTicketEvent>
        | undefined;

// The consolidated call event stores only the key of its CallRecord (the record itself lives in the
// canonical call-records store); the card resolves the record from this.
export const selectEventCallRecordKey = (
    state: State,
    ticketKey: TicketKey,
    eventKey: OmniCallEventKey,
) =>
    selectCallEventDataStatic(state, ticketKey, eventKey)?.get('callRecordKey');

// The call record behind a ticket's call, for consumers that have only the ticket key. v3 sends at
// most one call event per ticket (home caps it in limitToSingleCallSession), so the first match is
// the ticket's call. Memoization is shared across tickets, as with
// selectFindMostRecentEventOutgoingKey below — see the note there.
export const selectTicketCallRecordKey = createSelector(
    [selectEvents],
    (events) =>
        (
            events?.find((event: TypedMap<TicketEvent>) =>
                eventIsCallRecordEvent(event.get('DTOSubtype')),
            ) as TypedMap<CallRecordTicketEvent> | undefined
        )?.get('callRecordKey'),
);

export const selectEventSenderData = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) =>
    selectMessageEventDataStatic(state, ticketKey, eventKey)?.get('senderData');

export const selectEventSender = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => selectEventSenderData(state, ticketKey, eventKey)?.get('sender');

export const selectEventSenderType = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => selectEventSenderData(state, ticketKey, eventKey)?.get('senderType');

export const selectEventDirection = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketEventKey,
) => selectEventDataStatic(state, ticketKey, eventKey)?.get('direction');

const selectEventDataDynamic = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => state.tickets.getIn(['ticketEvents', 'dataDynamic', ticketKey, eventKey]);

export const selectMessageDeliveryStatus = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => selectEventDataDynamic(state, ticketKey, eventKey)?.get('deliveryStatus');

export const selectMessageDeliveryStatusIsSending = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => selectMessageDeliveryStatus(state, ticketKey, eventKey) === 'SENDING';

export const selectTicketMessageAttachments = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) => selectEventDataDynamic(state, ticketKey, eventKey)?.get('attachments');

export const selectEventNumberOfAttachments = (
    state: State,
    ticketKey: TicketKey,
    eventKey: ConversationMessageKey,
) =>
    selectMessageEventDataStatic(state, ticketKey, eventKey)?.get(
        'numberOfAttachments',
    );

export const makeSelectMostRecentIncomingEmailKey = () =>
    createSelector([selectEvents], (events) =>
        events
            ?.filter(
                (event) =>
                    event.get('DTOSubtype') === 'OmniEmailMessageTicketEvent' &&
                    event.get('direction') === 'INCOMING',
            )
            ?.maxBy((event) => event.get('timeStamp'))
            ?.get('key'),
    );

const selectFindMostRecentEventOutgoingKey = createSelector(
    [selectEvents],
    (events) =>
        events
            ?.filter((event) => event.get('direction') === 'OUTGOING')
            ?.maxBy((event) => event.get('timeStamp'))
            ?.get('key'),
);
// About method above:
// The reason we don't create a unique instance of this selector despite it taking a custom key, i.e., TicketKey,
// is that we don't really worry about memoization breaking if this somehow were to be accessed in multiple tickets
// at the same time. I guess the one scenario that happens is when a related ticket is opened (in a modal = two ticket
// event feeds mounted at the same time: the one in the modal, and the one in the background)

export const selectIsMostRecentEvent = (
    state: State,
    ticketKey: TicketKey,
    eventKey: TicketEventKey,
) => selectFindMostRecentEventOutgoingKey(state, ticketKey) === eventKey;
